import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { LOGO_LIGHT } from "../components/Navbar";
import { ShieldCheck, GoogleLogo, AppleLogo, CheckCircle } from "@phosphor-icons/react";

const BUILDING = "https://images.unsplash.com/photo-1623428997047-296a86d28a20?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

function SidePanel({ points }) {
  return (
    <div className="relative hidden lg:block">
      <img src={BUILDING} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-navy-950/80 to-navy-950/40" />
      <div className="absolute bottom-12 left-12 right-12">
        <p className="font-display text-3xl text-white leading-tight max-w-md">EBG Group's exclusive franchise partner platform.</p>
        <ul className="mt-8 space-y-3">
          {points.map((p) => (
            <li key={p} className="flex items-center gap-3 text-ebg-muted text-sm"><CheckCircle size={18} className="text-gold" weight="fill" /> {p}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mobile");
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [phone, setPhone] = useState(""); const [code, setCode] = useState(""); const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const after = (data) => { setUser(data); toast.success("Welcome back"); navigate(data.role === "admin" ? "/admin" : "/dashboard"); };

  const submitEmail = async (e) => { e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/login", { email, password }); after(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); } finally { setLoading(false); } };

  const requestOtp = async () => { if (!phone) return toast.error("Enter your mobile number"); setLoading(true);
    try { const { data } = await api.post("/auth/otp/request", { phone }); setOtpSent(true); toast.success(`OTP sent. Demo code: ${data.dev_code}`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); } };

  const verifyOtp = async (e) => { e.preventDefault(); setLoading(true);
    try { const { data } = await api.post("/auth/otp/verify", { phone, code }); after(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); } };

  const inputCls = "w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-navy-950 placeholder:text-gray-400 focus:border-gold focus:ring-1 focus:ring-gold focus:outline-none transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy-950">
      <SidePanel points={["Secure & Trusted Platform", "Transparent Process", "Backed by EBG Group"]} />
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl">
            <img src={LOGO_LIGHT} alt="EBG Franchise Partner" className="h-9 w-auto mx-auto mb-7" />
            <h1 className="font-display text-4xl text-navy-950 text-center">Welcome Back</h1>
            <p className="text-center text-sm text-gray-500 mt-2">New to EBG Franchise Partner? <Link to="/register" className="text-gold font-medium hover:underline">Create an account</Link></p>

            <div className="flex gap-1 mt-7 bg-gray-100 rounded-lg p-1">
              <button onClick={() => setTab("mobile")} data-testid="login-tab-otp" className={`flex-1 text-sm py-2 rounded-md transition-colors ${tab === "mobile" ? "bg-white text-navy-950 shadow font-medium" : "text-gray-500"}`}>Mobile</button>
              <button onClick={() => setTab("email")} data-testid="login-tab-email" className={`flex-1 text-sm py-2 rounded-md transition-colors ${tab === "email" ? "bg-white text-navy-950 shadow font-medium" : "text-gray-500"}`}>Email</button>
            </div>

            {tab === "mobile" ? (
              <form onSubmit={verifyOtp} className="mt-6 space-y-4" data-testid="login-otp-form">
                <div>
                  <label className="text-xs font-medium text-gray-600">Mobile Number</label>
                  <div className="flex gap-2 mt-1.5">
                    <span className="grid place-items-center px-3 bg-gray-50 border border-gray-200 rounded-lg text-navy-950 text-sm">+91</span>
                    <input data-testid="login-phone-input" type="tel" required placeholder="Enter your mobile number" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
                  </div>
                </div>
                {otpSent && <input data-testid="login-otp-input" type="text" required placeholder="6-digit OTP" value={code} onChange={(e) => setCode(e.target.value)} className={inputCls} />}
                {!otpSent
                  ? <button type="button" onClick={requestOtp} data-testid="login-send-otp-btn" disabled={loading} className="w-full btn-gold py-3 rounded-lg disabled:opacity-50">{loading ? "Sending…" : "Send OTP"}</button>
                  : <button data-testid="login-verify-otp-btn" disabled={loading} className="w-full btn-gold py-3 rounded-lg disabled:opacity-50">{loading ? "Verifying…" : "Verify & Login"}</button>}
              </form>
            ) : (
              <form onSubmit={submitEmail} className="mt-6 space-y-4" data-testid="login-email-form">
                <input data-testid="login-email-input" type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                <input data-testid="login-password-input" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
                <button data-testid="login-submit-btn" disabled={loading} className="w-full btn-gold py-3 rounded-lg disabled:opacity-50">{loading ? "Signing in…" : "Login"}</button>
              </form>
            )}

            <div className="flex items-center gap-3 my-6"><span className="flex-1 h-px bg-gray-200" /><span className="text-xs text-gray-400">or continue with</span><span className="flex-1 h-px bg-gray-200" /></div>
            <div className="flex gap-3">
              <button className="flex-1 border border-gray-200 rounded-lg py-2.5 grid place-items-center hover:bg-gray-50 transition-colors"><GoogleLogo size={20} className="text-navy-950" /></button>
              <button className="flex-1 border border-gray-200 rounded-lg py-2.5 grid place-items-center hover:bg-gray-50 transition-colors"><AppleLogo size={20} className="text-navy-950" weight="fill" /></button>
            </div>
            <p className="text-center text-xs text-gray-400 mt-6 flex items-center justify-center gap-1.5"><ShieldCheck size={14} className="text-gold" /> By continuing you agree to our Terms & Privacy Policy.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
